// Recursive compile-time resolver for `.luis` imports.
// It enforces directive order and keeps `.css` imports as-is.
import type { LuisCompileWarning, LuisCompileOptions } from '../types'
import { createFlatResolvedImportSource } from './line-map'
import type { LuisSourceLineOrigin } from './line-map-types'
import { resolveSourceId } from './resolve-utils'
import { resolveSourceRecursive } from './resolve-source'
import type { ImportResolveContext } from './types'
import { resolveResourceLimits } from '../resource-limits'

// Resolve `.luis` imports recursively and return merged source + warnings.
export function resolveLuisImportsInternal(
  source: string,
  options: LuisCompileOptions,
): { source: string; warnings: LuisCompileWarning[]; lineMap: LuisSourceLineOrigin[] } {
  const rootSourceId = resolveSourceId(options.sourceId, '<inline>')
  // Return early when source has no directive tokens to resolve.
  if (
    !source.includes('@import')
    && !source.includes('@char')
    && !source.includes('@charset')
    && !source.includes('@layer')
  ) {
    // Return early for source without import directives.
    return { ...createFlatResolvedImportSource(source, rootSourceId), warnings: [] }
  } else {
    // Continue recursive directive scan and resolution.
  }

  const context: ImportResolveContext = {
    options,
    warnings: [],
    variableOwners: new Map<string, string>(),
    importStack: [rootSourceId],
    importCount: 0,
    importedBytes: 0,
    resourceLimits: resolveResourceLimits(options),
  }

  const resolved = resolveSourceRecursive(source, rootSourceId, context)
  // Return resolved source and collected diagnostics.
  return { ...resolved, warnings: context.warnings }
}
